import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import { showToast } from '../../Components/ToastNotifications';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toTitleCase } from '../../utils/textFormat';

const PROGRAM_GOALS_OPTIONS = [
  'Meningkatkan omzet / pendapatan BUMDes',
  'Memperluas pasar / jangkauan pelanggan',
  'Meningkatkan kualitas produk atau layanan',
  'Memperbaiki tata kelola dan manajemen BUMDes',
  'Mendapatkan pendampingan dan tenaga ahli',
  'Meningkatkan daya saing terhadap kompetitor',
];

const PBC_SUPPORT_OPTIONS = [
  'Pemasaran dan promosi produk',
  'Digitalisasi operasional (media sosial, marketplace, dll)',
  'Pembukuan dan keuangan',
  'Peningkatan kualitas produk',
  'Jaringan dan kemitraan bisnis',
  'Pelatihan dan peningkatan kapasitas SDM',
];

const POSITION_OPTIONS = [
  { value: 'Direktur/CEO/Ketua BUMDesa', label: 'Direktur/CEO/Ketua BUMDesa' },
];

const LOCAL_STORAGE_KEY = 'bumdes_portal_presurvey_data';

const BumdesPreSurvey = () => {
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingAutofill, setIsEditingAutofill] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState({});
  const [districts, setDistricts] = useState({});
  const [villages, setVillages] = useState({});
  const [originalAutofillData, setOriginalAutofillData] = useState(null);

  const totalSteps = 3;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      fullName: authUser ? `${authUser.fullName || ''}`.trim() : '',
      position: '',
      birthDate: '',
      age: '',
      bumdesName: '',
      provinceId: '',
      provinceName: '',
      regenciesId: '',
      regencyName: '',
      districtsId: '',
      districtName: '',
      villagesId: '',
      villageName: '',
      establishmentYear: '',
      registrationNumber: '',
      bumdesType: '',
      totalAdministrators: 1,
      administratorsData: [{ name: '', position: '', age: '' }],
      whatsapp: '',
      email: authUser?.email || '',
      attendanceStatus: '',
      isWillingToJoin: '',
      unwillingReason: '',
      isWillingToAcceptPbc: '',
      pbcRequirements: '',
      isWillingToCommit: '',
      programGoals: [],
      biggestChallenge: '',
      neededSupportFromPbc: [],
      additionalNotes: '',
      autofillCorrections: { isCorrected: false },
    },
  });

  const {
    fields: adminFields,
    append: appendAdmin,
    remove: removeAdmin,
  } = useFieldArray({
    control,
    name: 'administratorsData',
  });

  const watchBirthDate = watch('birthDate');
  const watchIsWillingToJoin = watch('isWillingToJoin');
  const watchIsWillingToAcceptPbc = watch('isWillingToAcceptPbc');
  const watchProgramGoals = watch('programGoals');
  const watchNeededSupportFromPbc = watch('neededSupportFromPbc');

  // Sync totalAdministrators with number of listed admins
  useEffect(() => {
    setValue('totalAdministrators', adminFields.length);
  }, [adminFields, setValue]);

  // Age calculation
  useEffect(() => {
    if (watchBirthDate) {
      const birth = new Date(watchBirthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      setValue('age', age >= 0 ? age : 0, { shouldValidate: true });
    }
  }, [watchBirthDate, setValue]);

  // Fetch autofill from backend and detect submission status
  useEffect(() => {
    const fetchAutofill = async () => {
      try {
        const response = await api.getBumdesPreSurveyAutofill();
        if (response?.data) {
          const apiData = response.data;
          setOriginalAutofillData(apiData);
          setValue('autofillCorrections', { isCorrected: false });

          if (apiData.isPreSurveyDone) {
            setSubmitted(true);
            return;
          }

          // Exclude the Direktur entry — they're already accounted for as
          // the person filling out this pre-survey (see narrative below).
          const administratorsDataExcludingDirektur = (
            apiData.administratorsData || []
          ).filter((admin) => !/direktur/i.test(admin.position || ''));
          const hasAdministratorsData =
            administratorsDataExcludingDirektur.length > 0;

          const mappedData = {
            ...apiData,
            position: apiData.role || apiData.position,
            provinceId: apiData.domicilies?.province?.id,
            provinceName: apiData.domicilies?.province?.name,
            regenciesId: apiData.domicilies?.regency?.id,
            regencyName: apiData.domicilies?.regency?.name,
            districtsId: apiData.domicilies?.district?.id,
            districtName: apiData.domicilies?.district?.name,
            villagesId: apiData.domicilies?.village?.id,
            villageName: apiData.domicilies?.village?.name,
          };

          // Prefill "Daftar Pengurus & Umur" from the same
          // administrators_data captured earlier at /bumdes/register.
          // Keep the default single blank row when there's nothing to prefill.
          if (hasAdministratorsData) {
            mappedData.administratorsData =
              administratorsDataExcludingDirektur.map((admin) => ({
                name: admin.name || '',
                position: admin.position || '',
                age:
                  admin.age !== undefined && admin.age !== null
                    ? String(admin.age)
                    : '',
              }));
          } else {
            delete mappedData.administratorsData;
          }

          reset((prev) => ({
            ...prev,
            ...mappedData,
          }));
        }
      } catch (error) {
        console.error('Failed to fetch autofill data', error);
      }
    };

    fetchAutofill();
  }, [reset]);

  // Watch if autofilled fields were changed
  const currentBumdesName = watch('bumdesName');
  const currentVillageId = watch('villagesId');
  const currentDistrictId = watch('districtsId');
  const currentRegencyId = watch('regenciesId');
  const currentProvinceId = watch('provinceId');
  const currentYear = watch('establishmentYear');
  const currentRegNumber = watch('registrationNumber');
  const currentType = watch('bumdesType');

  useEffect(() => {
    if (originalAutofillData) {
      const origProvId = originalAutofillData.domicilies?.province?.id;
      const origRegId = originalAutofillData.domicilies?.regency?.id;
      const origDistId = originalAutofillData.domicilies?.district?.id;
      const origVilId = originalAutofillData.domicilies?.village?.id;

      const isChanged =
        currentBumdesName !== originalAutofillData.bumdesName ||
        currentProvinceId !== origProvId ||
        currentRegencyId !== origRegId ||
        currentDistrictId !== origDistId ||
        currentVillageId !== origVilId ||
        String(currentYear) !==
          String(originalAutofillData.establishmentYear) ||
        currentRegNumber !== originalAutofillData.registrationNumber ||
        currentType !== originalAutofillData.bumdesType;

      if (isChanged) {
        setValue('autofillCorrections', {
          isCorrected: true,
          bumdesName: currentBumdesName,
          provinceName: watch('provinceName'),
          provinceId: currentProvinceId,
          regencyName: watch('regencyName'),
          regencieId: currentRegencyId,
          districtName: watch('districtName'),
          districtId: currentDistrictId,
          villageName: watch('villageName'),
          villageId: currentVillageId,
          establishmentYear: currentYear,
          registrationNumber: currentRegNumber,
          bumdesType: currentType,
        });
      } else {
        setValue('autofillCorrections', { isCorrected: false });
      }
    }
  }, [
    currentBumdesName,
    currentVillageId,
    currentDistrictId,
    currentRegencyId,
    currentProvinceId,
    currentYear,
    currentRegNumber,
    currentType,
    originalAutofillData,
    setValue,
  ]);

  // Regional dropdown loading
  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const data = await api.getProvincies();
        setProvinces(data);
      } catch (error) {
        console.error('Failed to fetch provinces', error);
      }
    };
    fetchProvinces();
  }, []);

  useEffect(() => {
    if (currentProvinceId && !regencies[currentProvinceId]) {
      const fetchRegencies = async () => {
        try {
          const data = await api.getRegenciesByProvincies({
            id: currentProvinceId,
          });
          setRegencies((prev) => ({ ...prev, [currentProvinceId]: data }));
        } catch (error) {
          console.error('Failed to fetch regencies', error);
        }
      };
      fetchRegencies();
    }
  }, [currentProvinceId, regencies]);

  useEffect(() => {
    if (currentRegencyId && !districts[currentRegencyId]) {
      const fetchDistricts = async () => {
        try {
          const data = await api.getDistrictsByRegencies({
            id: currentRegencyId,
          });
          setDistricts((prev) => ({ ...prev, [currentRegencyId]: data }));
        } catch (error) {
          console.error('Failed to fetch districts', error);
        }
      };
      fetchDistricts();
    }
  }, [currentRegencyId, districts]);

  useEffect(() => {
    if (currentDistrictId && !villages[currentDistrictId]) {
      const fetchVillages = async () => {
        try {
          const data = await api.getVillagesByDistricts({
            id: currentDistrictId,
          });
          setVillages((prev) => ({ ...prev, [currentDistrictId]: data }));
        } catch (error) {
          console.error('Failed to fetch villages', error);
        }
      };
      fetchVillages();
    }
  }, [currentDistrictId, villages]);

  // Fields required to complete the current step (single source of truth,
  // reused both for the Next-button click validation and for the reactive
  // disabled-state check below).
  const currentStepFields = useMemo(() => {
    if (currentStep === 1) {
      return [
        'fullName',
        'position',
        'birthDate',
        isEditingAutofill ? 'provinceId' : 'provinceName',
        isEditingAutofill ? 'regenciesId' : 'regencyName',
        isEditingAutofill ? 'districtsId' : 'districtName',
        isEditingAutofill ? 'villagesId' : 'villageName',
      ];
    }
    if (currentStep === 2) {
      return [
        'isWillingToJoin',
        'unwillingReason',
        'isWillingToAcceptPbc',
        'pbcRequirements',
        'isWillingToCommit',
        'biggestChallenge',
      ];
    }
    return ['programGoals', 'neededSupportFromPbc'];
  }, [currentStep, isEditingAutofill]);

  // Reactively track whether all required fields on the current step are
  // filled, so the Next/Submit button can be disabled until they are —
  // instead of only catching missing required fields when the user clicks.
  const [isStepValid, setIsStepValid] = useState(false);

  useEffect(() => {
    let isActive = true;
    const revalidate = () => {
      trigger(currentStepFields).then((valid) => {
        if (isActive) setIsStepValid(valid);
      });
    };
    revalidate();
    const subscription = watch(revalidate);
    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepFields]);

  const handleNext = async () => {
    const isValid = await trigger(currentStepFields);

    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const isValidFinal = await trigger([
      'programGoals',
      'biggestChallenge',
      'neededSupportFromPbc',
    ]);
    if (!isValidFinal) {
      setIsSubmitting(false);
      return;
    }

    // Format Payload exactly like public Pre-Survey BUMDes
    const payload = JSON.parse(JSON.stringify(data));

    // Simplify Payload
    delete payload.provinceId;
    delete payload.provinceName;
    delete payload.regenciesId;
    delete payload.regencyName;
    delete payload.districtsId;
    delete payload.districtName;
    delete payload.villagesId;
    delete payload.villageName;
    delete payload.establishmentYear;
    delete payload.registrationNumber;
    delete payload.bumdesType;
    delete payload.bumdesName;

    // Set Relation ID
    payload.institutionId =
      originalAutofillData?.institutionId ||
      data.institutionId ||
      authUser?.activeSession?.institutionId ||
      authUser?.activeSession?.batchId;

    // Handle Nullable / Skipped fields if Q1 is 'Tidak bersedia'
    if (payload.isWillingToJoin === 'Tidak bersedia') {
      payload.isWillingToAcceptPbc = null;
      payload.isWillingToCommit = null;
      payload.pbcRequirements = null;
    }

    // Handle Nullable / Skipped Q3 if Q2 is 'Tidak bersedia'
    if (payload.isWillingToAcceptPbc === 'Tidak bersedia') {
      payload.isWillingToCommit = null;
    }

    // Status Logic
    let readinessStatus = 'Perlu Tindak Lanjut';
    if (
      payload.isWillingToJoin === 'Ya, bersedia' &&
      payload.isWillingToAcceptPbc === 'Ya, bersedia' &&
      payload.isWillingToCommit === 'Ya, bersedia'
    ) {
      readinessStatus = 'Siap Program';
    }
    payload.readinessStatus = readinessStatus;

    try {
      let response;
      if (originalAutofillData?.isPreSurveyDone) {
        response = await api.updatePreSurveyBumdes({ payload });
      } else {
        response = await api.submitPreSurveyBumdes({ payload });
      }

      if (response.status === 'success') {
        showToast('Form Pre-Survey Berhasil Dikirim!', 'success');
        setSubmitted(true);
        // Memperbarui state secara lokal (Local State Update) agar saat "Ubah Jawaban"
        // diklik, form terisi dengan jawaban terakhir tanpa harus fetch ulang ke server.
        setOriginalAutofillData((prev) => ({
          ...prev,
          ...payload,
          isPreSurveyDone: true,
        }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        showToast('Gagal mengirim data: ' + response.message, 'danger');
      }
    } catch (error) {
      showToast('Terjadi kesalahan jaringan saat mengirim survey.', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepLabel = (step) => {
    const titles = [
      '',
      'I. Identitas & Kehadiran',
      'II. Kesediaan Program',
      'III. Ekspektasi & Komitmen',
    ];
    return titles[step];
  };

  if (submitted) {
    return (
      <div className='container py-4'>
        <div className='row justify-content-center'>
          <div className='col-lg-10'>
            <div className='card border-0 shadow-sm rounded-4 p-5 text-center bg-white mt-4 animate__animated animate__fadeIn'>
              <i className='bi bi-check-circle-fill text-success display-1 mb-4 animate__animated animate__bounceIn'></i>
              <h2 className='fw-bold'>Pre-Survey Selesai</h2>
              <p className='text-muted mb-4 max-w-md mx-auto'>
                Terima kasih atas komitmen BUMDes Anda untuk mengikuti program
                pemberdayaan kami. Data kesiapan dan dukungan Anda telah
                berhasil direkam secara aman ke dalam sistem kami.
              </p>
              <div className='d-flex justify-content-center gap-3'>
                <button
                  className='btn btn-primary px-4 py-2 rounded-pill shadow-sm fw-bold'
                  onClick={() => navigate('/bumdes/home')}
                >
                  <i className='bi bi-house-door me-2'></i> Buka Dashboard
                </button>
                {authUser?.activeSession?.isSurveyVeciDone === false && (
                  <button
                    className='btn btn-outline-secondary px-4 py-2 rounded-pill shadow-sm fw-bold'
                    onClick={() => {
                      setSubmitted(false);
                      const mappedData = {
                        ...originalAutofillData,
                        position:
                          originalAutofillData?.role ||
                          originalAutofillData?.position,
                        provinceId:
                          originalAutofillData?.domicilies?.province?.id,
                        provinceName:
                          originalAutofillData?.domicilies?.province?.name,
                        regenciesId:
                          originalAutofillData?.domicilies?.regency?.id,
                        regencyName:
                          originalAutofillData?.domicilies?.regency?.name,
                        districtsId:
                          originalAutofillData?.domicilies?.district?.id,
                        districtName:
                          originalAutofillData?.domicilies?.district?.name,
                        villagesId:
                          originalAutofillData?.domicilies?.village?.id,
                        villageName:
                          originalAutofillData?.domicilies?.village?.name,
                      };
                      reset((prev) => ({
                        ...prev,
                        ...mappedData,
                      }));
                    }}
                  >
                    <i className='bi bi-pencil-square me-2'></i> Ubah Jawaban
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container py-4'>
      <div className='row justify-content-center'>
        <div className='col-lg-10'>
          {/* Gorgeous Indicator Header inspired by VECIHeader */}
          <div className='card border-0 shadow-sm mb-4 overflow-hidden rounded-4'>
            <div className='card-body p-4 bg-primary text-white position-relative'>
              <div className='position-relative z-1'>
                <h2 className='fw-bold mb-1'>Pre-Survey Audiensi BUMDes</h2>
                <p className='opacity-75 mb-0 small'>
                  Formulir Penutupan Sesi Audiensi & Konfirmasi Komitmen Program
                  Pendampingan
                </p>
              </div>
              <div className='position-absolute top-0 end-0 p-4 opacity-25'>
                <i className='bi bi-patch-question display-1'></i>
              </div>
            </div>
            <div className='bg-light p-3'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 rounded-pill fw-bold'>
                  {getStepLabel(currentStep)}
                </span>
                <span className='text-muted small fw-semibold'>
                  Langkah {currentStep} dari {totalSteps}
                </span>
              </div>
              <div
                className='progress rounded-pill'
                style={{ height: '8px' }}
              >
                <div
                  className='progress-bar progress-bar-striped progress-bar-animated bg-primary'
                  role='progressbar'
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Form Card */}
          <div className='card card-body border-0 shadow-sm rounded-4 bg-white p-4 p-md-5'>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* STEP 1: IDENTITAS & KEHADIRAN */}
              {currentStep === 1 && (
                <div className='fade-in'>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-dark'>
                        Nama Lengkap Direktur{' '}
                        <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='text'
                        className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                        {...register('fullName', { required: 'Wajib diisi' })}
                      />
                      {errors.fullName && (
                        <div className='invalid-feedback'>
                          {errors.fullName.message}
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-dark'>
                        Jabatan di BUMDes <span className='text-danger'>*</span>
                      </label>
                      <Controller
                        name='position'
                        control={control}
                        rules={{ required: 'Wajib diisi' }}
                        render={({ field }) => (
                          <CreatableSelect
                            {...field}
                            isClearable
                            options={POSITION_OPTIONS}
                            placeholder='-- Pilih atau Ketik Jabatan --'
                            classNamePrefix='react-select'
                            className={errors.position ? 'is-invalid' : ''}
                            onChange={(option) =>
                              field.onChange(option ? option.value : '')
                            }
                            value={
                              POSITION_OPTIONS.find(
                                (opt) => opt.value === field.value,
                              ) ||
                              (field.value
                                ? { value: field.value, label: field.value }
                                : null)
                            }
                            formatCreateLabel={(inputValue) =>
                              `Tambah jabatan: "${inputValue}"`
                            }
                          />
                        )}
                      />
                      {errors.position && (
                        <div className='text-danger small mt-1 ml-1'>
                          {errors.position.message}
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-dark'>
                        Tanggal Lahir <span className='text-danger'>*</span>
                      </label>
                      <input
                        type='date'
                        className={`form-control ${errors.birthDate ? 'is-invalid' : ''}`}
                        {...register('birthDate', { required: 'Wajib diisi' })}
                      />
                      {errors.birthDate && (
                        <div className='invalid-feedback'>
                          {errors.birthDate.message}
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold text-dark'>
                        Umur
                      </label>
                      <input
                        type='number'
                        className='form-control bg-light'
                        readOnly
                        {...register('age')}
                      />
                      <div className='form-text text-success small'>
                        <i className='bi bi-check-circle'></i> Dihitung otomatis
                        dari tanggal lahir
                      </div>
                    </div>

                    <div className='col-12 mt-5'>
                      <h5 className='fw-bold mb-3 border-bottom pb-2 text-dark'>
                        <i className='bi bi-shop me-2 text-primary'></i>
                        Informasi BUMDes
                      </h5>
                      <p className='text-muted small mb-3'>
                        Berikut adalah data BUMDes Anda yang tercatat saat
                        registrasi. Centang kotak di bawah jika Anda ingin
                        mengoreksi data ini.
                      </p>
                    </div>

                    <div className='col-md-12'>
                      <label className='form-label fw-semibold text-dark'>
                        Nama BUMDes Anda
                      </label>
                      <input
                        type='text'
                        className='form-control bg-light'
                        readOnly={!isEditingAutofill}
                        {...register('bumdesName', {
                          onBlur: (e) =>
                            setValue('bumdesName', toTitleCase(e.target.value)),
                        })}
                      />
                    </div>

                    <div className='col-12 px-3'>
                      <div className='form-check p-3 bg-light rounded border shadow-sm'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          id='editAutofillToggle'
                          checked={isEditingAutofill}
                          onChange={(e) =>
                            setIsEditingAutofill(e.target.checked)
                          }
                        />
                        <label
                          className='form-check-label fw-semibold text-primary'
                          htmlFor='editAutofillToggle'
                          style={{ cursor: 'pointer' }}
                        >
                          Saya ingin mengubah data BUMDes di bawah ini
                        </label>
                        <div className='form-text text-muted mt-1'>
                          Aktifkan pilihan ini jika terdapat kesalahan ketik
                          atau perubahan informasi pada detail badan usaha
                          BUMDes Anda.
                        </div>
                      </div>
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold'>
                        Provinsi <span className='text-danger'>*</span>
                      </label>
                      {!isEditingAutofill ? (
                        <input
                          type='text'
                          disabled
                          className='form-control bg-light'
                          value={watch('provinceName') || ''}
                        />
                      ) : (
                        <Controller
                          name='provinceId'
                          control={control}
                          rules={{ required: 'Wajib diisi' }}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-select ${errors.provinceId ? 'is-invalid' : ''}`}
                              onChange={(e) => {
                                const selectedVal = e.target.value
                                  ? Number(e.target.value)
                                  : '';
                                field.onChange(selectedVal);
                                const selectedProv = provinces.find(
                                  (p) => p.provinceId === selectedVal,
                                );
                                setValue(
                                  'provinceName',
                                  selectedProv ? selectedProv.provinceName : '',
                                );

                                // Reset child fields
                                setValue('regenciesId', '');
                                setValue('regencyName', '');
                                setValue('districtsId', '');
                                setValue('districtName', '');
                                setValue('villagesId', '');
                                setValue('villageName', '');
                              }}
                              value={field.value || ''}
                            >
                              <option value=''>Pilih Provinsi...</option>
                              {provinces.map((prov) => (
                                <option
                                  key={prov.provinceId}
                                  value={prov.provinceId}
                                >
                                  {prov.provinceName}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      )}
                      {errors.provinceId && (
                        <div className='text-danger extra-small mt-1'>
                          {errors.provinceId.message}
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold'>
                        Kabupaten / Kota <span className='text-danger'>*</span>
                      </label>
                      {!isEditingAutofill ? (
                        <input
                          type='text'
                          disabled
                          className='form-control bg-light'
                          value={watch('regencyName') || ''}
                        />
                      ) : (
                        <Controller
                          name='regenciesId'
                          control={control}
                          rules={{ required: 'Wajib diisi' }}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-select ${errors.regenciesId ? 'is-invalid' : ''}`}
                              disabled={!watch('provinceId')}
                              onChange={(e) => {
                                const selectedVal = e.target.value
                                  ? Number(e.target.value)
                                  : '';
                                field.onChange(selectedVal);
                                const list =
                                  regencies[watch('provinceId')] || [];
                                const selectedReg = list.find(
                                  (r) => r.regenciesId === selectedVal,
                                );
                                setValue(
                                  'regencyName',
                                  selectedReg ? selectedReg.regenciesName : '',
                                );

                                // Reset child fields
                                setValue('districtsId', '');
                                setValue('districtName', '');
                                setValue('villagesId', '');
                                setValue('villageName', '');
                              }}
                              value={field.value || ''}
                            >
                              <option value=''>Pilih Kabupaten/Kota...</option>
                              {(regencies[watch('provinceId')] || []).map(
                                (regency) => (
                                  <option
                                    key={regency.regenciesId}
                                    value={regency.regenciesId}
                                  >
                                    {regency.regenciesName}
                                  </option>
                                ),
                              )}
                            </select>
                          )}
                        />
                      )}
                      {errors.regenciesId && (
                        <div className='text-danger extra-small mt-1'>
                          {errors.regenciesId.message}
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold'>
                        Kecamatan <span className='text-danger'>*</span>
                      </label>
                      {!isEditingAutofill ? (
                        <input
                          type='text'
                          disabled
                          className='form-control bg-light'
                          value={watch('districtName') || ''}
                        />
                      ) : (
                        <Controller
                          name='districtsId'
                          control={control}
                          rules={{ required: 'Wajib diisi' }}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-select ${errors.districtsId ? 'is-invalid' : ''}`}
                              disabled={!watch('regenciesId')}
                              onChange={(e) => {
                                const selectedVal = e.target.value
                                  ? Number(e.target.value)
                                  : '';
                                field.onChange(selectedVal);
                                const list =
                                  districts[watch('regenciesId')] || [];
                                const selectedDist = list.find(
                                  (d) => d.districtsId === selectedVal,
                                );
                                setValue(
                                  'districtName',
                                  selectedDist
                                    ? selectedDist.districtsName
                                    : '',
                                );

                                // Reset child fields
                                setValue('villagesId', '');
                                setValue('villageName', '');
                              }}
                              value={field.value || ''}
                            >
                              <option value=''>Pilih Kecamatan...</option>
                              {(districts[watch('regenciesId')] || []).map(
                                (district) => (
                                  <option
                                    key={district.districtsId}
                                    value={district.districtsId}
                                  >
                                    {district.districtsName}
                                  </option>
                                ),
                              )}
                            </select>
                          )}
                        />
                      )}
                      {errors.districtsId && (
                        <div className='text-danger extra-small mt-1'>
                          {errors.districtsId.message}
                        </div>
                      )}
                    </div>

                    <div className='col-md-6'>
                      <label className='form-label fw-semibold'>
                        Desa <span className='text-danger'>*</span>
                      </label>
                      {!isEditingAutofill ? (
                        <input
                          type='text'
                          disabled
                          className='form-control bg-light'
                          value={watch('villageName') || ''}
                        />
                      ) : (
                        <Controller
                          name='villagesId'
                          control={control}
                          rules={{ required: 'Wajib diisi' }}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={`form-select ${errors.villagesId ? 'is-invalid' : ''}`}
                              disabled={!watch('districtsId')}
                              onChange={(e) => {
                                const selectedVal = e.target.value
                                  ? Number(e.target.value)
                                  : '';
                                field.onChange(selectedVal);
                                const list =
                                  villages[watch('districtsId')] || [];
                                const selectedVil = list.find(
                                  (v) => v.villagesId === selectedVal,
                                );
                                setValue(
                                  'villageName',
                                  selectedVil ? selectedVil.villagesName : '',
                                );
                              }}
                              value={field.value || ''}
                            >
                              <option value=''>Pilih Desa/Kelurahan...</option>
                              {(villages[watch('districtsId')] || []).map(
                                (village) => (
                                  <option
                                    key={village.villagesId}
                                    value={village.villagesId}
                                  >
                                    {village.villagesName}
                                  </option>
                                ),
                              )}
                            </select>
                          )}
                        />
                      )}
                      {errors.villagesId && (
                        <div className='text-danger extra-small mt-1'>
                          {errors.villagesId.message}
                        </div>
                      )}
                    </div>

                    <div className='col-md-4'>
                      <label className='form-label fw-semibold'>
                        Tahun Berdiri
                      </label>
                      <input
                        type='number'
                        disabled={!isEditingAutofill}
                        className='form-control'
                        {...register('establishmentYear')}
                      />
                    </div>

                    <div className='col-md-4'>
                      <label className='form-label fw-semibold'>
                        Nomor Registrasi
                      </label>
                      <input
                        type='text'
                        disabled={!isEditingAutofill}
                        className='form-control'
                        {...register('registrationNumber')}
                      />
                    </div>

                    <div className='col-md-4'>
                      <label className='form-label fw-semibold'>
                        Jenis BUMDes
                      </label>
                      <input
                        type='text'
                        disabled={!isEditingAutofill}
                        className='form-control'
                        {...register('bumdesType')}
                      />
                    </div>

                    {/* Dynamic Administrators Section */}
                    <div className='col-12 bg-light p-4 rounded-4 border shadow-sm'>
                      <div className='d-flex justify-content-between align-items-center mb-3 border-bottom pb-2'>
                        <h6 className='fw-bold text-dark mb-0'>
                          Daftar Pengurus & Umur
                        </h6>
                        <span className='badge bg-primary rounded-pill px-3 py-2'>
                          {adminFields.length} Terdaftar
                        </span>
                      </div>
                      <input
                        type='hidden'
                        {...register('totalAdministrators')}
                      />

                      <div className='d-flex align-items-center mb-3 text-muted'>
                        <i className='bi bi-info-circle me-2 small'></i>
                        <span className='small fw-medium'>
                          Tambahkan baris untuk mendaftarkan anggota pengurus
                          BUMDes lainnya yang hadir hari ini, di luar Direktur
                          (karena Direktur adalah Anda yang mengisi formulir
                          ini).
                        </span>
                      </div>

                      {adminFields.map((field, index) => (
                        <div
                          key={field.id}
                          className='position-relative bg-white p-2 rounded-3 border mb-2 animate__animated animate__fadeIn'
                        >
                          <div className='row g-2 align-items-center pe-4'>
                            <div
                              className='col-auto fw-bold text-primary small ps-2'
                              style={{ minWidth: '24px' }}
                            >
                              #{index + 1}
                            </div>
                            <div className='col-12 col-md'>
                              <input
                                type='text'
                                placeholder='Nama Pengurus'
                                className='form-control form-control-sm border-0 bg-light'
                                {...register(
                                  `administratorsData.${index}.name`,
                                  {
                                    required: true,
                                    onBlur: (e) =>
                                      setValue(
                                        `administratorsData.${index}.name`,
                                        toTitleCase(e.target.value),
                                      ),
                                  },
                                )}
                              />
                            </div>
                            <div className='col-8 col-md-3'>
                              <input
                                type='text'
                                placeholder='Jabatan (Contoh: Sekretaris)'
                                className='form-control form-control-sm border-0 bg-light'
                                {...register(
                                  `administratorsData.${index}.position`,
                                  {
                                    required: true,
                                  },
                                )}
                              />
                            </div>
                            <div className='col-4 col-md-2'>
                              <input
                                type='number'
                                placeholder='Umur'
                                className='form-control form-control-sm border-0 bg-light'
                                {...register(
                                  `administratorsData.${index}.age`,
                                  {
                                    required: true,
                                  },
                                )}
                              />
                            </div>
                          </div>
                          {index > 0 && (
                            <button
                              type='button'
                              className='btn btn-link text-danger position-absolute p-0'
                              style={{
                                top: '50%',
                                right: '8px',
                                transform: 'translateY(-50%)',
                                zIndex: 10,
                              }}
                              onClick={() => removeAdmin(index)}
                              title='Hapus Pengurus'
                            >
                              <i className='bi bi-trash fs-6'></i>
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type='button'
                        className='btn btn-outline-primary btn-sm w-100 py-2 fw-semibold rounded-3 mt-2'
                        onClick={() =>
                          appendAdmin({ name: '', position: '', age: '' })
                        }
                      >
                        <i className='bi bi-plus-circle me-2'></i>Tambah
                        Pengurus
                      </button>
                    </div>

                  </div>
                </div>
              )}

              {/* STEP 2: KESEDIAAN PROGRAM */}
              {currentStep === 2 && (
                <div className='fade-in'>
                  <div className='alert alert-info mb-4 border-info shadow-sm rounded-4 p-3 d-flex align-items-start'>
                    <i className='bi bi-info-circle-fill me-3 fs-4 text-info'></i>
                    <div>
                      <span className='fw-semibold'>
                        Pernyataan berikut berkaitan dengan komitmen BUMDes
                      </span>{' '}
                      untuk mengikuti Program Peningkatan Daya Saing BUMDes oleh
                      BCF & PFI.
                    </div>
                  </div>

                  <div className='mb-4 bg-white p-4 rounded-4 border shadow-sm'>
                    <label className='form-label fw-bold fs-5 mb-3 text-dark'>
                      1. Apakah BUMDes Bapak/Ibu berminat untuk mengikuti
                      Program Peningkatan Daya Saing BUMDes?{' '}
                      <span className='text-danger'>*</span>
                    </label>
                    <div className='ms-md-3'>
                      {[
                        'Ya, bersedia',
                        'Masih mempertimbangkan',
                        'Tidak bersedia',
                      ].map((val) => (
                        <div
                          className='form-check mb-3'
                          key={val}
                        >
                          <input
                            className='form-check-input form-check-input-lg'
                            type='radio'
                            value={val}
                            id={`join-${val}`}
                            {...register('isWillingToJoin', {
                              required: 'Pilih salah satu',
                            })}
                          />
                          <label
                            className='form-check-label fs-5 ps-1'
                            htmlFor={`join-${val}`}
                          >
                            {val}
                          </label>
                        </div>
                      ))}
                    </div>
                    {errors.isWillingToJoin && (
                      <div className='text-danger small mt-1 ms-3 fw-bold'>
                        {errors.isWillingToJoin.message}
                      </div>
                    )}

                    {(watchIsWillingToJoin === 'Masih mempertimbangkan' ||
                      watchIsWillingToJoin === 'Tidak bersedia') && (
                      <div className='mt-4 p-4 bg-light shadow-inner border-start border-4 border-warning rounded'>
                        <label className='form-label fw-semibold text-dark'>
                          Apa alasan utama BUMDes Anda masih mempertimbangkan
                          atau belum berminat mengikuti program ini?{' '}
                          <span className='text-danger'>*</span>
                        </label>
                        <p className='text-muted small mb-2'>
                          Jawaban ini bersifat konfidensial dan hanya digunakan
                          untuk evaluasi program oleh BCF.
                        </p>
                        <textarea
                          className={`form-control ${errors.unwillingReason ? 'is-invalid' : ''}`}
                          rows='3'
                          {...register('unwillingReason', {
                            required:
                              "Wajib diisi jika opsi bukan 'Ya, bersedia'",
                          })}
                        ></textarea>
                        {errors.unwillingReason && (
                          <div className='invalid-feedback'>
                            {errors.unwillingReason.message}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {watchIsWillingToJoin !== 'Tidak bersedia' && (
                    <div className='mb-4 bg-white p-4 rounded-4 border shadow-sm animate__animated animate__fadeIn'>
                      <label className='form-label fw-bold fs-5 mb-2 text-dark'>
                        2. Apakah BUMDes Bapak/Ibu bersedia untuk menerima dan
                        mendukung kehadiran project officer (po) yang
                        ditempatkan di BUMDes selama program berlangsung?{' '}
                        <span className='text-danger'>*</span>
                      </label>
                      <p className='text-muted small mb-3 border-bottom pb-3'>
                        Project-based contractor adalah tenaga pendamping
                        profesional muda yang akan bekerja bersama pengurus
                        BUMDes selama durasi program.
                      </p>
                      <div className='ms-md-3'>
                        {[
                          'Ya, bersedia',
                          'Bersedia dengan syarat tertentu',
                          'Tidak bersedia',
                        ].map((val) => (
                          <div
                            className='form-check mb-3'
                            key={val}
                          >
                            <input
                              className='form-check-input form-check-input-lg'
                              type='radio'
                              value={val}
                              id={`pbc-${val}`}
                              {...register('isWillingToAcceptPbc', {
                                required:
                                  watchIsWillingToJoin !== 'Tidak bersedia'
                                    ? 'Pilih salah satu'
                                    : false,
                              })}
                            />
                            <label
                              className='form-check-label fs-5 ps-1'
                              htmlFor={`pbc-${val}`}
                            >
                              {val}
                            </label>
                          </div>
                        ))}
                      </div>
                      {errors.isWillingToAcceptPbc && (
                        <div className='text-danger small mt-1 ms-3 fw-bold'>
                          {errors.isWillingToAcceptPbc.message}
                        </div>
                      )}

                      {watchIsWillingToAcceptPbc ===
                        'Bersedia dengan syarat tertentu' && (
                        <div className='mt-4 p-4 bg-light shadow-inner border-start border-4 border-info rounded'>
                          <label className='form-label fw-semibold text-dark'>
                            Jika ada syarat atau catatan terkait penempatan PBC
                            di BUMDes Anda, mohon jelaskan. *
                          </label>
                          <textarea
                            className={`form-control ${errors.pbcRequirements ? 'is-invalid' : ''}`}
                            rows='3'
                            {...register('pbcRequirements', {
                              required:
                                watchIsWillingToAcceptPbc ===
                                'Bersedia dengan syarat tertentu'
                                  ? 'Wajib diisi syarat'
                                  : false,
                            })}
                          ></textarea>
                          {errors.pbcRequirements && (
                            <div className='invalid-feedback'>
                              {errors.pbcRequirements.message}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {watchIsWillingToJoin !== 'Tidak bersedia' &&
                    watchIsWillingToAcceptPbc !== 'Tidak bersedia' && (
                      <div className='mb-4 bg-white p-4 rounded-4 border shadow-sm animate__animated animate__fadeIn'>
                        <label className='form-label fw-bold fs-5 mb-3 text-dark'>
                          3. Apakah BUMDes Bapak/Ibu memiliki komitmen untuk
                          mengikuti rangkaian program peningkatan daya saing
                          BUMDes dengan durasi kurang lebih 6 bulan?{' '}
                          <span className='text-danger'>*</span>
                        </label>
                        <div className='ms-md-3'>
                          {[
                            'Ya, bersedia',
                            'Bersedia sebagian aktivitas',
                            'Tidak bersedia',
                          ].map((val) => (
                            <div
                              className='form-check mb-3'
                              key={val}
                            >
                              <input
                                className='form-check-input form-check-input-lg'
                                type='radio'
                                value={val}
                                id={`commit-${val}`}
                                {...register('isWillingToCommit', {
                                  required:
                                    watchIsWillingToJoin !== 'Tidak bersedia' &&
                                    watchIsWillingToAcceptPbc !==
                                      'Tidak bersedia'
                                      ? 'Pilih komitmen'
                                      : false,
                                })}
                              />
                              <label
                                className='form-check-label fs-5 ps-1'
                                htmlFor={`commit-${val}`}
                              >
                                {val}
                              </label>
                            </div>
                          ))}
                        </div>
                        {errors.isWillingToCommit && (
                          <div className='text-danger small mt-1 ms-3 fw-bold'>
                            {errors.isWillingToCommit.message}
                          </div>
                        )}
                      </div>
                    )}

                  {watchIsWillingToJoin !== 'Tidak bersedia' &&
                    watchIsWillingToAcceptPbc !== 'Tidak bersedia' && (
                      <div className='mb-4 bg-white p-4 rounded-4 border shadow-sm animate__animated animate__fadeIn'>
                        <label className='form-label fw-bold fs-5 mb-2 text-dark'>
                          4. Tantangan terbesar yang saat ini dihadapi BUMDes
                          Anda dalam mengembangkan usaha adalah:{' '}
                          <span className='text-danger'>*</span>
                        </label>
                        <p className='text-muted small mb-3 border-bottom pb-3'>
                          Jawaban singkat 1–3 kalimat sudah cukup. Tidak perlu
                          jawaban panjang.
                        </p>
                        <textarea
                          className={`form-control ${errors.biggestChallenge ? 'is-invalid' : ''}`}
                          rows='3'
                          placeholder='Tuliskan tantangan di sini...'
                          {...register('biggestChallenge', {
                            required:
                              watchIsWillingToJoin !== 'Tidak bersedia' &&
                              watchIsWillingToAcceptPbc !== 'Tidak bersedia'
                                ? 'Wajib diisi tantangan'
                                : false,
                          })}
                        ></textarea>
                        {errors.biggestChallenge && (
                          <div className='invalid-feedback fw-bold'>
                            {errors.biggestChallenge.message}
                          </div>
                        )}
                      </div>
                    )}
                </div>
              )}

              {/* STEP 3: EKSPEKTASI PROGRAM */}
              {currentStep === 3 && (
                <div className='fade-in'>
                  <div className='mb-5 bg-white p-4 rounded-4 border shadow-sm'>
                    <label className='form-label fw-bold fs-5 mb-3 text-dark'>
                      1. Apa tujuan utama yang ingin BUMDes Anda capai jika
                      mengikuti program peningkatan daya saing BUMDes? (Pilih
                      maksimal 2) <span className='text-danger'>*</span>
                    </label>
                    <div className='ms-md-3 mt-3'>
                      {PROGRAM_GOALS_OPTIONS.map((goal, idx) => (
                        <div
                          className='form-check mb-3 px-0 d-flex align-items-center'
                          key={idx}
                        >
                          <input
                            className='form-check-input form-check-input-lg mx-3'
                            type='checkbox'
                            value={goal}
                            id={`goal-${idx}`}
                            {...register('programGoals', {
                              validate: (v) => {
                                if (!v || v.length === 0)
                                  return 'Pilih minimal 1';
                                if (v.length > 2) return 'Pilih maksimal 2';
                                return true;
                              },
                            })}
                          />
                          <label
                            className='form-check-label fs-5'
                            htmlFor={`goal-${idx}`}
                          >
                            {goal}
                          </label>
                        </div>
                      ))}

                      <div className='form-check px-0 d-flex align-items-center mt-4 bg-light p-3 rounded-3 border'>
                        <label className='form-label mb-0 ms-2 me-3 fw-semibold'>
                          Lainnya:
                        </label>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Sebutkan...'
                          onChange={(e) => {
                            const val = e.target.value;
                            const filtered =
                              watchProgramGoals?.filter(
                                (g) => !g.startsWith('Lainnya:'),
                              ) || [];
                            if (val) {
                              setValue(
                                'programGoals',
                                [...filtered, `Lainnya: ${val}`],
                                { shouldValidate: true },
                              );
                            } else {
                              setValue('programGoals', filtered, {
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                    {errors.programGoals && (
                      <div className='text-danger mt-2 ms-3 fw-bold'>
                        {errors.programGoals.message}
                      </div>
                    )}
                  </div>

                  <div className='mb-5 bg-white p-4 rounded-4 border shadow-sm'>
                    <label className='form-label fw-bold fs-5 mb-3 text-dark'>
                      2. Menurut Bapak/Ibu, dukungan seperti apa yang paling
                      dibutuhkan BUMDes Anda jika ada penempatan project officer
                      di lokasi BUMDes? <span className='text-danger'>*</span>
                    </label>
                    <div className='ms-md-3'>
                      {PBC_SUPPORT_OPTIONS.map((support, idx) => (
                        <div
                          className='form-check mb-3 px-0 d-flex align-items-center'
                          key={idx}
                        >
                          <input
                            className='form-check-input form-check-input-lg mx-3'
                            type='checkbox'
                            value={support}
                            id={`support-${idx}`}
                            {...register('neededSupportFromPbc', {
                              validate: (v) =>
                                v?.length > 0 || 'Pilih minimal 1',
                            })}
                          />
                          <label
                            className='form-check-label fs-5'
                            htmlFor={`support-${idx}`}
                          >
                            {support}
                          </label>
                        </div>
                      ))}

                      <div className='form-check px-0 d-flex align-items-center mt-4 bg-light p-3 rounded-3 border'>
                        <label className='form-label mb-0 ms-2 me-3 fw-semibold'>
                          Lainnya:
                        </label>
                        <input
                          type='text'
                          className='form-control'
                          placeholder='Sebutkan tambahan dukungan...'
                          onChange={(e) => {
                            const val = e.target.value;
                            const filtered =
                              watchNeededSupportFromPbc?.filter(
                                (g) => !g.startsWith('Lainnya:'),
                              ) || [];
                            if (val) {
                              setValue(
                                'neededSupportFromPbc',
                                [...filtered, `Lainnya: ${val}`],
                                { shouldValidate: true },
                              );
                            } else {
                              setValue('neededSupportFromPbc', filtered, {
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      </div>
                    </div>
                    {errors.neededSupportFromPbc && (
                      <div className='text-danger mt-2 ms-3 fw-bold'>
                        {errors.neededSupportFromPbc.message}
                      </div>
                    )}
                  </div>

                  <div className='mb-4 bg-white p-4 rounded-4 border shadow-sm'>
                    <label className='form-label fw-bold fs-5 mb-2 text-dark'>
                      3. Adakah hal lain yang ingin Bapak/Ibu sampaikan kepada
                      BCF?
                    </label>
                    <p className='text-muted small mb-3'>(Opsional)</p>
                    <textarea
                      className='form-control'
                      rows='3'
                      placeholder='Tuliskan harapan atau catatan tambahan lainnya...'
                      {...register('additionalNotes')}
                    ></textarea>
                  </div>
                </div>
              )}

              {/* Form Action Controls */}
              <div className='d-flex flex-column-reverse flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2 mt-5 pt-4 border-top'>
                {currentStep > 1 ? (
                  <button
                    type='button'
                    className='btn btn-outline-secondary rounded-pill px-4 py-2 fw-semibold'
                    onClick={handlePrev}
                  >
                    <i className='bi bi-arrow-left me-2'></i>Sebelumnya
                  </button>
                ) : (
                  <div className='d-none d-sm-block' />
                )}

                {currentStep < totalSteps ? (
                  <button
                    type='button'
                    className='btn btn-primary rounded-pill px-4 px-sm-5 py-2 fw-bold'
                    onClick={handleNext}
                    disabled={!isStepValid}
                    title={
                      !isStepValid
                        ? 'Lengkapi dahulu bidang yang wajib diisi (*)'
                        : ''
                    }
                  >
                    Selanjutnya <i className='bi bi-arrow-right ms-2'></i>
                  </button>
                ) : (
                  <button
                    type='submit'
                    className='btn btn-success text-white rounded-pill px-4 px-sm-5 py-2 fw-bold shadow-sm'
                    disabled={isSubmitting || !isStepValid}
                    title={
                      !isStepValid
                        ? 'Lengkapi dahulu bidang yang wajib diisi (*)'
                        : ''
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className='spinner-border spinner-border-sm me-2'
                          role='status'
                          aria-hidden='true'
                        ></span>
                        Sedang Mengirim...
                      </>
                    ) : (
                      <>
                        Kirim Form Pre-Survey{' '}
                        <i className='bi bi-send-check ms-2'></i>
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BumdesPreSurvey;
